import React, { useState } from 'react';
import { OffboardingAction, ActionStatus } from '../../types';
import Modal from '../Shared/Modal';
import UserSearch from './UserSearch';

interface ActionChecklistProps {
  actions: OffboardingAction[];
  onToggle: (actionId: string) => void;
  onUpdatePopupData: (actionId: string, data: Record<string, string>) => void;
  disabled?: boolean;
  /** Compte offboardé : écarté des résultats de recherche des destinataires */
  targetUserId?: string;
}

/** Destinataire retenu, avec possibilité d'en changer */
const SelectedRecipient: React.FC<{
  name?: string;
  email: string;
  onClear: () => void;
}> = ({ name, email, onClear }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-ms-gray-50 border border-ms-gray-200 rounded-md">
    <div className="min-w-0">
      <p className="text-sm font-medium text-ms-gray-800 truncate">{name || email}</p>
      {name && <p className="text-xs text-ms-gray-500 truncate">{email}</p>}
    </div>
    <button
      type="button"
      onClick={onClear}
      className="flex-shrink-0 text-xs text-ms-blue hover:text-ms-blue-dark px-2 py-1 rounded hover:bg-ms-blue-light transition-colors"
    >
      Changer
    </button>
  </div>
);

/** Icône de statut pour chaque action */
const StatusIcon: React.FC<{ status: ActionStatus }> = ({ status }) => {
  switch (status) {
    case 'running':
      return (
        <svg className="w-5 h-5 text-ms-blue animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      );
    case 'success':
      return <span className="text-ms-green text-lg">&#10003;</span>;
    case 'error':
      return <span className="text-ms-red text-lg">&#10007;</span>;
    case 'skipped':
      return <span className="text-ms-gray-400 text-sm">&#9197;&#65039;</span>;
    default:
      return null;
  }
};

/**
 * Checklist des actions d'offboarding
 * Chaque action peut être cochée/décochée
 * Les actions avec popup affichent un bouton de configuration
 */
const ActionChecklist: React.FC<ActionChecklistProps> = ({
  actions,
  onToggle,
  onUpdatePopupData,
  disabled = false,
  targetUserId,
}) => {
  const [popupAction, setPopupAction] = useState<OffboardingAction | null>(null);
  const [popupValues, setPopupValues] = useState<Record<string, string>>({});
  const [popupError, setPopupError] = useState<string | null>(null);

  const openPopup = (action: OffboardingAction) => {
    setPopupAction(action);
    setPopupValues(action.popupData || {});
    setPopupError(null);
  };

  /** Champ obligatoire de chaque action à popup */
  const requiredField: Record<string, { key: string; message: string }> = {
    delegateMailbox: { key: 'delegateEmail', message: 'Choisissez le délégué.' },
    forwardMail: { key: 'forwardTo', message: 'Choisissez le destinataire du transfert.' },
    setAutoReply: { key: 'message', message: "Saisissez le message d'absence." },
  };

  const savePopup = () => {
    if (!popupAction) return;

    // Valider ici plutôt qu'à l'exécution : une action cochée sans sa donnée
    // échouerait en plein offboarding, après des actions déjà appliquées.
    const required = requiredField[popupAction.id];
    if (required && !popupValues[required.key]?.trim()) {
      setPopupError(required.message);
      return;
    }

    onUpdatePopupData(popupAction.id, popupValues);
    // Activer automatiquement l'action quand les données popup sont remplies
    if (!popupAction.checked) {
      onToggle(popupAction.id);
    }
    setPopupAction(null);
    setPopupError(null);
  };

  /** Rendu du formulaire popup selon le type d'action */
  const renderPopupForm = () => {
    if (!popupAction) return null;

    switch (popupAction.id) {
      // Délégation : uniquement un compte du tenant. Add-MailboxPermission exige un
      // destinataire connu de l'organisation, une adresse externe est refusée par
      // Exchange — donc pas de saisie libre ici.
      case 'delegateMailbox':
        return (
          <div>
            <label className="block text-sm font-medium text-ms-gray-700 mb-2">
              Délégué
            </label>
            {popupValues.delegateEmail ? (
              <SelectedRecipient
                name={popupValues.delegateName}
                email={popupValues.delegateEmail}
                onClear={() =>
                  setPopupValues({ ...popupValues, delegateEmail: '', delegateName: '' })
                }
              />
            ) : (
              <UserSearch
                inline
                label=""
                placeholder="Nom, email ou UPN du délégué..."
                excludeUserId={targetUserId}
                onSelectUser={(user) => {
                  setPopupError(null);
                  setPopupValues({
                    ...popupValues,
                    delegateEmail: user.mail || user.userPrincipalName,
                    delegateName: user.displayName,
                  });
                }}
              />
            )}
            <p className="text-xs text-ms-gray-400 mt-2">
              Cette personne aura un accès complet (FullAccess) à la boîte mail. Elle doit
              posséder une boîte dans le tenant : Exchange refuse la délégation vers une
              adresse externe.
            </p>
          </div>
        );

      // Transfert : un compte du tenant dans la quasi-totalité des cas, mais
      // ForwardingSmtpAddress accepte une adresse externe — d'où la sortie de secours.
      case 'forwardMail':
        return (
          <div>
            <label className="block text-sm font-medium text-ms-gray-700 mb-2">
              Destinataire du transfert
            </label>
            {popupValues.forwardTo ? (
              <SelectedRecipient
                name={popupValues.forwardName}
                email={popupValues.forwardTo}
                onClear={() => setPopupValues({ ...popupValues, forwardTo: '', forwardName: '' })}
              />
            ) : (
              <UserSearch
                inline
                label=""
                placeholder="Nom, email ou UPN du destinataire..."
                excludeUserId={targetUserId}
                onSelectUser={(user) => {
                  setPopupError(null);
                  setPopupValues({
                    ...popupValues,
                    forwardTo: user.mail || user.userPrincipalName,
                    forwardName: user.displayName,
                  });
                }}
                onUseRawAddress={(address) => {
                  setPopupError(null);
                  setPopupValues({ ...popupValues, forwardTo: address, forwardName: '' });
                }}
              />
            )}
            <p className="text-xs text-ms-gray-400 mt-2">
              Les mails seront transférés ET conservés dans la boîte d'origine.
            </p>
            {popupValues.forwardTo && !popupValues.forwardName && (
              <p className="text-xs text-amber-700 mt-1">
                Adresse saisie manuellement, hors du tenant : vérifiez qu'un transfert
                vers l'extérieur est autorisé.
              </p>
            )}
          </div>
        );

      case 'setAutoReply':
        return (
          <div>
            <label className="block text-sm font-medium text-ms-gray-700 mb-1">
              Message d'absence
            </label>
            <textarea
              value={popupValues.message || ''}
              onChange={(e) => setPopupValues({ ...popupValues, message: e.target.value })}
              placeholder="Bonjour, cette personne ne fait plus partie de l'entreprise. Pour toute demande, veuillez contacter..."
              rows={4}
              className="w-full px-3 py-2 border border-ms-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ms-blue/30 focus:border-ms-blue resize-none"
            />
            <p className="text-xs text-ms-gray-400 mt-1">
              Ce message sera envoyé automatiquement à tous les expéditeurs (internes et externes).
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-white border border-ms-gray-200 rounded-lg divide-y divide-ms-gray-100">
        <div className="px-6 py-3 bg-ms-gray-50 rounded-t-lg">
          <h4 className="text-sm font-semibold text-ms-gray-700">Actions d'offboarding</h4>
        </div>

        {actions.map((action) => (
          <div
            key={action.id}
            className={`flex items-center gap-4 px-6 py-3.5 ${
              action.status === 'error' ? 'bg-red-50' : ''
            }`}
          >
            {/* Checkbox */}
            <input
              type="checkbox"
              checked={action.checked}
              onChange={() => onToggle(action.id)}
              disabled={disabled || action.status !== 'pending'}
              className="w-4 h-4 text-ms-blue rounded border-ms-gray-300 focus:ring-ms-blue disabled:opacity-50"
            />

            {/* Label et description */}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${action.checked ? 'text-ms-gray-800' : 'text-ms-gray-400'}`}>
                {action.label}
              </p>
              <p className="text-xs text-ms-gray-400">{action.description}</p>
              {action.errorMessage && (
                <p className="text-xs text-ms-red mt-1">{action.errorMessage}</p>
              )}
              {action.infoMessage && (
                <p className="text-xs text-ms-gray-500 mt-1">{action.infoMessage}</p>
              )}
              {/* Afficher les données popup configurées */}
              {action.requiresPopup && action.popupData && Object.values(action.popupData).some(v => v) && (
                <p className="text-xs text-ms-blue mt-1">
                  {action.id === 'delegateMailbox' && action.popupData.delegateEmail &&
                    `Délégué : ${action.popupData.delegateName || action.popupData.delegateEmail}`}
                  {action.id === 'forwardMail' && action.popupData.forwardTo &&
                    `Transfert vers : ${action.popupData.forwardName || action.popupData.forwardTo}`}
                  {action.id === 'setAutoReply' && action.popupData.message && 'Message configuré'}
                </p>
              )}
            </div>

            {/* Bouton configurer pour les actions avec popup */}
            {action.requiresPopup && action.status === 'pending' && (
              <button
                onClick={() => openPopup(action)}
                disabled={disabled}
                className="text-xs text-ms-blue hover:text-ms-blue-dark px-2 py-1 rounded hover:bg-ms-blue-light transition-colors disabled:opacity-50"
              >
                Configurer
              </button>
            )}

            {/* Statut */}
            <div className="w-6 flex justify-center">
              <StatusIcon status={action.status} />
            </div>
          </div>
        ))}
      </div>

      {/* Modal de configuration */}
      <Modal
        isOpen={!!popupAction}
        onClose={() => {
          setPopupAction(null);
          setPopupError(null);
        }}
        title={popupAction?.label || ''}
        onConfirm={savePopup}
        confirmLabel="Enregistrer"
      >
        {renderPopupForm()}
        {popupError && <p className="text-sm text-ms-red mt-3">{popupError}</p>}
      </Modal>
    </>
  );
};

export default ActionChecklist;
